import {
  PricingPlanProduct as ApiPricingPlanProduct,
  PricingPlanProductPricing as ApiPricing,
  ProductPricingRule as ApiRule,
} from '@vendasta/billing';
import { BillingStrategy, billingStrategyFromAPI } from './billing-strategy';
import { Commitment, commitmentFromAPI } from './commitment';
import { Currency } from './currency';
import { Frequency, frequencyFromApi, frequencyToApi } from './frequency';
import { ProductPricingRules, productPricingRulesFromApi } from './product-pricing-rules';
import { ProductPricingType, productPricingTypeFromApi, productPricingTypeToApi } from './product-pricing-type';
import { Fee, feesFromApi, feesToApi } from './fee';

export interface CurrencyPricingMap {
  [key: string]: PricingPlanProductPricing;
}

export interface PricingPlanProductPricing {
  type: ProductPricingType;
  rules: ProductPricingRules[];
  setupFee: number;
  fees: Fee[];
  frequency?: Frequency;
  currencyCode?: string;
}

export interface PricingPlanProduct {
  pricingPlanId: string;
  sku: string;
  frequency?: Frequency;
  pricing: CurrencyPricingMap;
  commitment: Commitment;
  created: Date;
  modified: Date;
  strategy: BillingStrategy;
  volumeCommitment: number;
  volumeCommitmentStartsAt?: Date;
  volumeCommitmentEndsAt?: Date;
  defaultStrategy?: BillingStrategy;
}

export function pricingPlanProductFromApi(apiProduct: ApiPricingPlanProduct): PricingPlanProduct {
  const pricingMap: CurrencyPricingMap = {};
  for (const apiCurrency in apiProduct.pricing) {
    if (!Object.prototype.hasOwnProperty.call(apiProduct.pricing, apiCurrency)) {
      continue;
    }

    const pricingApi = apiProduct.pricing[apiCurrency];
    pricingMap[apiCurrency] = {
      type: productPricingTypeFromApi(pricingApi.type),
      rules: productPricingRulesFromApi(pricingApi.rules),
      setupFee: pricingApi.setupFee || 0,
      fees: feesFromApi(pricingApi.fees),
      frequency: frequencyFromApi(pricingApi.frequency),
    };
  }

  return {
    pricingPlanId: apiProduct.pricingPlanId,
    sku: apiProduct.sku,
    frequency: frequencyFromApi(apiProduct.frequency),
    pricing: pricingMap,
    commitment: commitmentFromAPI(apiProduct.commitment),
    created: apiProduct.created,
    modified: apiProduct.modified,
    strategy: billingStrategyFromAPI(apiProduct.strategy),
    volumeCommitment: apiProduct.volumeCommitment,
    volumeCommitmentStartsAt:
      apiProduct.volumeCommitmentStartsAt?.getTime() !== 0 ? apiProduct.volumeCommitmentStartsAt : undefined,
    volumeCommitmentEndsAt:
      apiProduct.volumeCommitmentEndsAt?.getTime() !== 0 ? apiProduct.volumeCommitmentEndsAt : undefined,
  };
}

export function currencyPricingMapToApi(currencyPricingMap: CurrencyPricingMap | undefined): {
  [key: string]: ApiPricing;
} {
  if (!currencyPricingMap) {
    return {};
  }
  return Object.keys(currencyPricingMap)
    .filter((c) => c in Currency)
    .reduce((apiPricingMap: { [key: string]: ApiPricing }, c: string) => {
      apiPricingMap[c] = pricingPlanProductPricingToApi(currencyPricingMap[c]);
      return apiPricingMap;
    }, {});
}

export function productPricingRulesToApi(rules: ProductPricingRules[]): ApiRule[] {
  return rules.map((rule) => new ApiRule(rule));
}

export function pricingPlanProductPricingToApi(pricing: PricingPlanProductPricing): ApiPricing {
  return new ApiPricing({
    type: productPricingTypeToApi(pricing.type),
    rules: productPricingRulesToApi(pricing.rules),
    setupFee: pricing.setupFee,
    fees: feesToApi(pricing.fees),
    frequency: frequencyToApi(pricing.frequency),
    currencyCode: pricing.currencyCode,
  });
}
